// src/supabaseClient.js
import { createClient } from '@supabase/supabase-js';

/**
 * Supabase Client Configuration
 *
 * Ondersteunt zowel de nieuwe publishable key (sb_publishable_...) als de legacy anon key.
 * De nieuwe publishable key heeft voordelen:
 * - Onafhankelijk te roteren zonder downtime
 * - Betere security (wordt automatisch geblokkeerd in onveilige contexten)
 * - Geen JWT expiry problemen
 *
 * @see https://supabase.com/docs/guides/api/api-keys
 */

// Supabase URL (verplicht)
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

// API Key: Prefereer nieuwe publishable key, fallback naar legacy anon key
// De publishable key (sb_publishable_...) is de nieuwe standaard
// De anon key (eyJ...) is de legacy JWT-based key
const supabaseApiKey =
  import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY;

// Valideer environment variables
if (!supabaseUrl) {
  console.error('[Supabase] VITE_SUPABASE_URL is niet geconfigureerd');
  throw new Error('Missing VITE_SUPABASE_URL environment variable');
}

if (!supabaseApiKey) {
  console.error(
    '[Supabase] Geen API key geconfigureerd. Gebruik VITE_SUPABASE_PUBLISHABLE_KEY (aanbevolen) of VITE_SUPABASE_ANON_KEY (legacy)',
  );
  throw new Error(
    'Missing Supabase API key. Set VITE_SUPABASE_PUBLISHABLE_KEY or VITE_SUPABASE_ANON_KEY',
  );
}

// Waarschuw in development als nog de legacy anon key wordt gebruikt
const isLegacyAnonKey = supabaseApiKey.startsWith('eyJ');

if (import.meta.env.DEV && isLegacyAnonKey) {
  console.warn(
    '[Supabase] Using legacy anon key. Consider migrating to publishable key for better security.',
  );
}

// Supabase client configuratie met security en stability best practices
const supabaseOptions = {
  auth: {
    // Gebruik localStorage voor session persistence (stabiel over page refreshes)
    persistSession: true,
    storageKey: 'bem-auth-token',
    // Auto refresh token voor expiry
    autoRefreshToken: true,
    // Detecteer sessie in andere tabs
    detectSessionInUrl: true,
    // PKCE flow voor betere security
    flowType: 'pkce',
  },
  global: {
    // Request headers voor debugging en identificatie
    headers: {
      'x-client-info': 'bem-frontend/1.0',
    },
  },
  // Realtime configuratie voor betere stability
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
  // Database configuratie
  db: {
    schema: 'public',
  },
};

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseApiKey, supabaseOptions);

/**
 * Controleer een wachtwoord zonder de sessie van de app te vervangen.
 *
 * `supabase.auth.signInWithPassword` vervangt de huidige sessie door een nieuwe aal1-sessie.
 * Met 2FA aan weigert Supabase daarna `updateUser({ password })` (dat vraagt aal2). Daarom
 * gebruiken we een losse client die niets opslaat en de proefsessie meteen weer intrekt.
 * @returns {Promise<{ error: Error | null }>}
 */
export const verifyPassword = async (email, password) => {
  const memoryStorage = new Map();
  const checkClient = createClient(supabaseUrl, supabaseApiKey, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
      storageKey: 'bem-password-check',
      storage: {
        getItem: (key) => memoryStorage.get(key) ?? null,
        setItem: (key, value) => memoryStorage.set(key, value),
        removeItem: (key) => memoryStorage.delete(key),
      },
    },
  });
  const { error } = await checkClient.auth.signInWithPassword({ email, password });
  if (!error) {
    // Alleen deze proefsessie intrekken, niet de sessies van de gebruiker
    await checkClient.auth.signOut({ scope: 'local' }).catch(() => {});
  }
  return { error };
};
